import React, {Suspense, lazy, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import type {SimpleKeyboard} from 'react-simple-keyboard';

const VirtualKeyboard = lazy(() =>
  import('./VirtualKeyboard').then(m => ({default: m.VirtualKeyboard})),
);

interface VirtualKeyboardPortalProps {
  onKeyPress: (input: string) => void;
  wrapperRef: React.RefObject<HTMLDivElement>;
  keyboardRef: React.MutableRefObject<SimpleKeyboard | null>;
  layoutName?: 'default' | 'shift' | 'alt';
  className?: string;
  isVisible: boolean;
  onToggle: () => void;
  onChange: (value: string) => void;
}

export function VirtualKeyboardPortal({
  onKeyPress,
  wrapperRef,
  keyboardRef,
  layoutName = 'default',
  className = 'w-full h-full',
  isVisible,
  onToggle,
  onChange,
}: VirtualKeyboardPortalProps) {
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      console.warn('VirtualKeyboardPortal: Element #root did not found');
      return;
    }

    const container = document.createElement('div');
    container.id = 'virtual-keyboard-portal';
    container.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 9999;
      background: white;
      transform: translateY(100%);
      transition: transform 0.3s ease-in-out;
      will-change: transform;
    `;

    rootElement.parentNode?.insertBefore(container, rootElement.nextSibling);
    setPortalContainer(container);

    return () => {
      container.remove();
    };
  }, []);

  useEffect(() => {
    if (!portalContainer) return;
    portalContainer.style.transform = `translateY(${isVisible ? '0' : '100%'})`;
  }, [isVisible, portalContainer]);

  if (!portalContainer) {
    return null;
  }

  return createPortal(
    <Suspense fallback={null}>
      <VirtualKeyboard
        onKeyPress={onKeyPress}
        wrapperRef={wrapperRef}
        keyboardRef={keyboardRef}
        layoutName={layoutName}
        className={className}
        isVisible={isVisible}
        onToggle={onToggle}
        onChange={onChange}
      />
    </Suspense>,
    portalContainer,
  );
}
